/**
 * Centralized LIFF Service
 * Singleton pattern for LINE LIFF SDK management
 *
 * Usage:
 * import liffService from '@/services/liff';
 *
 * // In router guard or component
 * await liffService.init();
 * const profile = liffService.profile;
 */

const LIFF_ID = '2006378234-72e9GVNa';

export interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl: string;
  statusMessage?: string;
}

class LiffService {
  private _initialized = false;
  private _profile: LiffProfile | null = null;
  private _initPromise: Promise<void> | null = null;

  /**
   * Check if LIFF is initialized
   */
  get isInitialized(): boolean {
    return this._initialized;
  }

  /**
   * Check if user is logged in
   */
  get isLoggedIn(): boolean {
    if (!this._initialized) return false;
    return window.liff.isLoggedIn();
  }

  /**
   * Check if running inside LINE app
   */
  get isInClient(): boolean {
    if (!this._initialized) return false;
    return window.liff.isInClient();
  }

  /**
   * Get cached user profile
   */
  get profile(): LiffProfile | null {
    return this._profile;
  }

  /**
   * Get user ID (shortcut)
   */
  get userId(): string {
    return this._profile?.userId || '';
  }

  /**
   * Initialize LIFF SDK (idempotent - safe to call multiple times)
   */
  async init(): Promise<void> {
    // Return existing promise if initialization is in progress
    if (this._initPromise) {
      return this._initPromise;
    }

    // Skip if already initialized
    if (this._initialized) {
      return;
    }

    this._initPromise = this._doInit();

    try {
      await this._initPromise;
    } finally {
      this._initPromise = null;
    }
  }

  private async _doInit(): Promise<void> {
    try {
      await window.liff.init({ liffId: LIFF_ID });
      this._initialized = true;

      // Auto-fetch profile if logged in
      if (this.isLoggedIn) {
        await this.fetchProfile();
      }
    } catch (error) {
      console.error('LIFF initialization failed:', error);
      throw error;
    }
  }

  /**
   * Require user login - redirects if not logged in
   * Returns true if already logged in, never returns if redirect happens
   */
  requireLogin(): boolean {
    if (!this._initialized) {
      console.error('LIFF not initialized. Call init() first.');
      return false;
    }

    if (this.isLoggedIn) {
      return true;
    }

    window.liff.login();
    return false; // This line won't execute if redirect happens
  }

  /**
   * Fetch user profile from LINE
   */
  async fetchProfile(): Promise<LiffProfile | null> {
    if (!this._initialized || !this.isLoggedIn) {
      return null;
    }

    try {
      const profile = await window.liff.getProfile();
      this._profile = {
        userId: profile.userId || '',
        displayName: profile.displayName || '',
        pictureUrl: profile.pictureUrl || '',
        statusMessage: profile.statusMessage || '',
      };
      return this._profile;
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      return null;
    }
  }

  /**
   * Send message in LINE chat
   */
  async sendMessage(text: string): Promise<boolean> {
    if (!this._initialized || !this.isInClient) {
      console.warn('Cannot send message: not in LINE client');
      return false;
    }

    try {
      await window.liff.sendMessages([
        {
          type: 'text',
          text: text,
        },
      ]);
      return true;
    } catch (error) {
      console.error('Failed to send message:', error);
      return false;
    }
  }

  /**
   * Close LIFF window
   */
  closeWindow(): void {
    if (this._initialized && this.isInClient) {
      window.liff.closeWindow();
    }
  }

  /**
   * Send message and close window
   */
  async sendMessageAndClose(text: string): Promise<void> {
    await this.sendMessage(text);
    this.closeWindow();
  }

  /**
   * Open share target picker for Flex Messages
   * @param messages - LIFF message array (Flex, Text, etc.)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async shareTargetPicker(messages: any): Promise<void> {
    if (!this._initialized) {
      throw new Error('LIFF not initialized');
    }

    try {
      await window.liff.shareTargetPicker(messages);
    } catch (error) {
      console.error('Share target picker failed:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    if (this._initialized && this.isLoggedIn) {
      window.liff.logout();
      this._profile = null;
    }
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    if (!this._initialized || !this.isLoggedIn) {
      return null;
    }
    return window.liff.getAccessToken();
  }

  /**
   * Reset service state (for testing)
   */
  reset(): void {
    this._initialized = false;
    this._profile = null;
    this._initPromise = null;
  }
}

// Export singleton instance
const liffService = new LiffService();
export default liffService;

// Also export class for testing
export { LiffService };
